import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { ageTicks, fanXDomain, fanYMaxAcross, findCrossing, scrubIndexAt } from '../chart/fanDomain'
import { bandPath, linePath } from '../chart/geometry'
import { linearScale, niceTicks } from '../chart/scale'
import type { LinearScale } from '../chart/scale'
import type { SimResult } from '../engine/types'
import { useAnimatedFan } from '../hooks/useAnimatedFan'
import type { FanFrame } from '../hooks/useAnimatedFan'
import { useElementSize } from '../hooks/useElementSize'
import { formatINRCompact, formatINRTick, formatPercent } from '../utils/formatINR'

/**
 * The fan chart.
 *
 * This is the thing the brief asks to be "beautiful enough to screenshot on its
 * own", so a few decisions are worth stating outright:
 *
 *  - **Encoding.** With one layer the bands are a *sequential* scale — one hue,
 *    lightness ramping inward — because they encode one quantity (how likely)
 *    rather than several categories. The only other colour is the gold used for
 *    the FIRE requirement, and it appears nowhere else in the product, so the eye
 *    finds the goal without being told.
 *  - **Grid vs threshold.** Gridlines are solid hairlines one shade off the
 *    surface. The only dashed lines are requirements — dashes mean "threshold"
 *    here, and nothing else on the chart is allowed to use them.
 *  - **Values are never tooltip-only.** The axis, the endpoint labels and the
 *    requirement label carry the numbers on their own; the scrubber is an
 *    enhancement for exploring, not the only way to read the chart.
 *  - **SVG, not canvas.** A handful of paths and a dozen labels. Canvas would buy
 *    nothing and cost crisp text, selectable labels and CSS-driven theming.
 *
 * ── Comparison mode ─────────────────────────────────────────────────────────
 * Two layers switch the encoding from sequential to categorical, because it is
 * now answering a different question. Four decisions came out of the plan gate,
 * each rendered before it was written (`docs/sketches/f4/encodings.html`):
 *
 *  1. **Same encoding for both scenarios, separated by weight.** The active one
 *     draws at roughly double the fill opacity with the heavier median glow. The
 *     rejected alternative gave the comparison a different *kind* of treatment —
 *     stroked band edges instead of fills — and its three strokes never cohered
 *     into a band at all. Asymmetry by weight, not by kind.
 *  2. **Neither distribution is hidden.** Drawing one scenario's spread and not
 *     the other's makes two heavily-overlapping ranges look like "B beats A",
 *     which for a probabilistic tool is a correctness problem rather than a
 *     stylistic one.
 *  3. **Each layer fades past its own target age** — see `fadeStops`.
 *  4. **Gold belongs to the scenario you are editing.** The comparison's crossing
 *     is a hollow ring in its own hue; two solid gold discs land ~15px apart on a
 *     360px screen and read as one smudge.
 */

export interface FanLayer {
  id: string
  result: SimResult | null
  /** A CSS custom property NAME, e.g. `--c-scenario-a`. Themes resolve it. */
  hueToken: string
  /** Age this layer's headline is quoted against; gets a marked rule. */
  targetAge: number
  /** Exactly one layer is active. It draws last, heaviest, and owns the scrubber. */
  active: boolean
  /** Short human label, for the accessible description. */
  label: string
}

interface FanChartProps {
  /**
   * One or two layers, in stable id order — **not** in draw order. Each is bound
   * to its own animation state by position, so reordering on focus change would
   * swap one scenario's fan onto the other's easing curve. Draw order comes from
   * `active`.
   */
  layers: FanLayer[]
  /** True while the fast tier is showing. Renders slightly dimmed. */
  isPreview?: boolean
  /** Plays the one-shot bloom at the active layer's marker. */
  celebrating?: boolean
  /**
   * Fixed height in pixels. Omit to fill the container instead, which is what
   * the product screen does — it lets CSS own the responsive height rather than
   * duplicating the breakpoint in JavaScript, and means the chart is correct at
   * every viewport width instead of at two.
   */
  height?: number
  className?: string
}

const MARGIN = { top: 18, right: 16, bottom: 30, left: 58 }
const MIN_WIDTH = 240
const MIN_HEIGHT = 180

/**
 * How far a fan is dimmed by the right edge, once past its own target age.
 *
 * The years after a target are accumulation with no withdrawals — a fiction until
 * F7 models the withdrawal phase — and `plan.ts` trimmed them deliberately for
 * exactly that reason. Two scenarios cannot both be trimmed, because they share
 * one x-domain and the shorter fan would end in a hard vertical edge partway
 * across the chart. A fade ends it instead of cutting it, and says what those
 * years are worth at the same time.
 *
 * It also produces something nobody designed: with targets at 50 and 57, the
 * earlier scenario is visibly dimmer between them, so the chart says "this one is
 * past its decision point and that one is not" for free.
 */
const FADE_FLOOR = 0.18

export function FanChart({
  layers,
  isPreview = false,
  celebrating = false,
  height: fixedHeight,
  className,
}: FanChartProps) {
  const uid = useId().replace(/:/g, '')
  const [containerRef, size] = useElementSize<HTMLDivElement>()
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [scrubIndex, setScrubIndex] = useState<number | null>(null)

  /*
   * Two hooks, always, bound to array POSITION rather than to the active layer.
   * React requires a fixed hook count, and two is the cap; more importantly, a
   * hook that changed which scenario it tracked would animate one fan's numbers
   * onto the other's shape for a few hundred milliseconds on every focus change.
   */
  /*
   * One y-domain for the set, handed to both hooks rather than maxed afterwards.
   * `fanYMaxAcross` evaluates the requirement term at the LATEST target instead
   * of at the end of the projection — the shared horizon runs well past the
   * earlier scenario's target, and reading the requirement out there was
   * crushing that fan into the bottom of the frame. Undefined with one layer, so
   * the arrival chart keeps `fanYMax` exactly as it was.
   */
  const settledResults = layers
    .map((layer) => layer.result)
    .filter((result): result is SimResult => result !== null)
  // Index within `settledResults`, so an unsettled comparison does not shift it.
  const activeSettledIndex = Math.max(
    0,
    layers.filter((l) => l.result !== null).findIndex((l) => l.active),
  )
  const sharedYMax =
    layers.length > 1 && settledResults.length > 0
      ? fanYMaxAcross(settledResults, activeSettledIndex)
      : undefined

  const frame0 = useAnimatedFan(layers[0]?.result ?? null, 90, sharedYMax)
  const frame1 = useAnimatedFan(layers[1]?.result ?? null, 90, sharedYMax)
  const frames = [frame0, frame1]

  const drawable = layers
    .map((layer, index) => ({ layer, frame: frames[index] }))
    // `!= null`, not `!== null`: `frames` is a fixed pair, so a third layer
    // yields `undefined` here and `!== null` would narrow it to FanFrame and
    // throw on `.yMax`. The prop type carries no length bound and
    // `--c-scenario-c` is already reserved.
    .filter(
      (entry): entry is { layer: FanLayer; frame: FanFrame } => entry.frame != null,
    )

  const width = Math.max(MIN_WIDTH, size.width)
  // Measured height when none is fixed. MIN_HEIGHT covers the first paint,
  // before the ResizeObserver has reported — without it the chart would render
  // once at zero height and visibly snap open.
  const height = fixedHeight ?? Math.max(MIN_HEIGHT, size.height)
  const plotWidth = Math.max(1, width - MARGIN.left - MARGIN.right)
  const plotHeight = Math.max(1, height - MARGIN.top - MARGIN.bottom)

  const active = drawable.find((entry) => entry.layer.active) ?? drawable[0]

  // Keyed on the Float32Array's identity, not on `frame`. The animation hook
  // returns a fresh wrapper object every frame but reuses the underlying arrays,
  // so keying on `frame` would rebuild this 40-element array 60 times a second
  // while looking like it was cached.
  const ageArray = active?.frame.ages
  const ages = useMemo(() => (ageArray ? Array.from(ageArray) : []), [ageArray])

  // `drawable` is a fresh array on every animation frame, so depending on it
  // directly would rebuild this callback 60 times a second — the same reason
  // `ages` above is keyed on the Float32Array's identity. A ref read only from an
  // event handler, never during render, keeps the callback identity stable.
  //
  // Written in an effect, not during render: a render React starts and throws away
  // — a transition, an offscreen pass — would otherwise leave the hit-test
  // inverting a domain nobody is looking at. The ref should hold the last
  // COMMITTED tree, and this is the same disagreement-between-two-views bug the
  // scrubber fix above is about, one layer down.
  const drawableRef = useRef(drawable)
  useEffect(() => {
    drawableRef.current = drawable
  })

  const handlePointer = useCallback(
    (event: React.PointerEvent<SVGSVGElement>) => {
      const entries = drawableRef.current
      if (entries.length === 0) return
      const rect = svgRef.current?.getBoundingClientRect()
      if (rect === undefined) return

      // The domain choice lives in scrubIndexAt, not here: the render and the
      // hit-test have to agree on it, and while this built its own scale from the
      // active layer alone they did not. Allocating the frame list per pointer
      // event is free — this runs on events, not on frames.
      setScrubIndex(
        scrubIndexAt(
          event.clientX - rect.left,
          [MARGIN.left, MARGIN.left + plotWidth],
          entries.map((entry) => entry.frame),
          Math.max(0, entries.findIndex((entry) => entry.layer.active)),
        ),
      )
    },
    [plotWidth],
  )

  const clearScrub = useCallback(() => setScrubIndex(null), [])

  if (active === undefined || ages.length === 0) {
    return (
      <div
        ref={containerRef}
        className={className}
        style={{ height: fixedHeight ?? '100%' }}
        role="img"
        aria-label="Projection loading"
      >
        <div className="flex h-full items-center justify-center text-[color:var(--c-text-muted)]">
          <span className="text-[length:var(--text-label)]">Running simulation…</span>
        </div>
      </div>
    )
  }

  // Everything below is recomputed every frame, deliberately and without
  // memoisation: `yMax` moves as the fan grows and the band contents are mutated
  // in place by the animation, so there is genuinely nothing here that survives a
  // frame. Wrapping these in useMemo would only advertise a cache that never hits.
  const comparing = drawable.length > 1

  /* Domains span EVERY layer. The shared horizon means the age arrays already
     match, but taking the union keeps a mid-animation length mismatch from
     clipping one fan against the other's extent — and it is shared with the
     hit-test through `fanXDomain` rather than recomputed, because the two
     disagreeing is exactly the defect that shipped in F4. */
  const [xMin, xMax] = fanXDomain(drawable.map((e) => e.frame))
  const yMax = Math.max(...drawable.map((e) => e.frame.yMax))

  const x = linearScale([xMin, xMax], [MARGIN.left, MARGIN.left + plotWidth])
  const y = linearScale([0, yMax], [MARGIN.top + plotHeight, MARGIN.top])

  const yTicks = niceTicks(0, yMax, 4)
  // Generated from the same union the ticks are PLACED on. Reading the active
  // layer's axis here was a third instance of the hit-test bug: during a length
  // mismatch the labels stopped short of the axis they were being scaled against.
  // `ageTicks` reads only the first and last entry, so the domain pair is the
  // whole input it needs.
  const xTicks = ageTicks([xMin, xMax])

  // Inactive first, so the scenario being edited is composited on top of the one
  // it is being compared against.
  const ordered = [...drawable].sort(
    (a, b) => Number(a.layer.active) - Number(b.layer.active),
  )

  const scrubAge = scrubIndex === null ? null : ages[Math.min(scrubIndex, ages.length - 1)]

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ position: 'relative', height: fixedHeight ?? '100%' }}
    >
      <svg
        ref={svgRef}
        width={width}
        height={height}
        // The chart is a figure with an accessible summary; the numbers are also
        // present as text elsewhere on the page for screen readers.
        role="img"
        aria-label={describe(drawable.map((e) => e.layer))}
        style={{
          display: 'block',
          touchAction: 'none',
          opacity: isPreview ? 0.92 : 1,
          transition: 'opacity var(--dur-fast) var(--ease-out-quart)',
        }}
        onPointerMove={handlePointer}
        onPointerDown={handlePointer}
        onPointerLeave={clearScrub}
        onPointerCancel={clearScrub}
      >
        <defs>
          {/*
            The y-axis is scaled to the target age, so past that point the p90
            band runs above the top of the frame. Without this it would paint
            over the headline sitting above the chart. Clipped to the plot rect
            so it exits cleanly at the top edge.
          */}
          <clipPath id={`${uid}-plot`}>
            <rect x={MARGIN.left} y={MARGIN.top} width={plotWidth} height={plotHeight} />
          </clipPath>

          {/* Bands brighten left-to-right so the fan reads as opening into the
              future rather than as a flat wedge. Single-layer only: comparison
              mode carries hue per scenario and cannot also spend it on depth. */}
          {!comparing && (
            <>
              <linearGradient id={`${uid}-outer`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="var(--c-fan-1)" stopOpacity="0.22" />
                <stop offset="100%" stopColor="var(--c-fan-1)" stopOpacity="0.62" />
              </linearGradient>
              <linearGradient id={`${uid}-inner`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="var(--c-fan-3)" stopOpacity="0.3" />
                <stop offset="100%" stopColor="var(--c-fan-3)" stopOpacity="0.78" />
              </linearGradient>
            </>
          )}

          {comparing &&
            ordered.map(({ layer }) => (
              <FadeMask
                key={layer.id}
                id={`${uid}-fade-${layer.id}`}
                from={x(clampAge(layer.targetAge, xMin, xMax))}
                to={MARGIN.left + plotWidth}
                rect={{
                  x: MARGIN.left,
                  y: MARGIN.top,
                  width: plotWidth,
                  height: plotHeight,
                }}
              />
            ))}
        </defs>

        {/* ── Grid: solid hairlines, one shade off the surface ── */}
        <g aria-hidden="true">
          {yTicks.map((tick) => (
            <line
              key={tick}
              x1={MARGIN.left}
              x2={MARGIN.left + plotWidth}
              y1={y(tick)}
              y2={y(tick)}
              stroke="var(--c-border-subtle)"
              strokeWidth={1}
              shapeRendering="crispEdges"
            />
          ))}
        </g>

        {/* ── The fans ── */}
        <g clipPath={`url(#${uid}-plot)`}>
          {ordered.map(({ layer, frame }) => (
            <g
              key={layer.id}
              data-scenario={layer.id}
              mask={comparing ? `url(#${uid}-fade-${layer.id})` : undefined}
            >
              {comparing ? (
                <ComparisonBands frame={frame} hue={layer.hueToken} active={layer.active} x={x} y={y} />
              ) : (
                <SoloBands frame={frame} uid={uid} x={x} y={y} />
              )}
            </g>
          ))}

          {/* ── Requirements: the only dashed lines on the chart ──
              One per layer. Scenarios that share expenses and inflation produce
              identical lines that coincide exactly and read as one; scenarios
              that differ genuinely have two thresholds, and hiding the second
              would quietly quote one scenario's goal against both.

              Each carries its OWN layer's fade mask, and still draws after every
              band. Before, these were unmasked siblings of the layer groups, so
              the gold threshold stayed fully lit across exactly the span the fade
              exists to discount — the one part of a layer that never dimmed past
              its target age.

              Masked individually rather than by moving each path inside its
              layer's group, which is the tempting one-line version and loses the
              z-order: the active fan's bands would then paint over the comparison's
              threshold, dimming the line this chart exists to be read against. */}
          {ordered.map(({ layer, frame }) => (
            <g
              key={`req-${layer.id}`}
              mask={comparing ? `url(#${uid}-fade-${layer.id})` : undefined}
            >
              <path
                d={linePath(frame.ages, frame.required, x, y)}
                fill="none"
                stroke="var(--c-fire)"
                strokeWidth={1.5}
                strokeDasharray="5 4"
                strokeOpacity={!comparing || layer.active ? 0.9 : 0.45}
              />
            </g>
          ))}
        </g>

        {/* ── Target-age rules ── */}
        {dedupeByAge(ordered).map(({ layer }) => {
          const rx = x(clampAge(layer.targetAge, xMin, xMax))
          return (
            <g key={`rule-${layer.id}`}>
              <line
                x1={rx}
                x2={rx}
                y1={MARGIN.top}
                y2={MARGIN.top + plotHeight}
                stroke={comparing ? `var(${layer.hueToken})` : 'var(--c-border-strong)'}
                strokeOpacity={comparing ? 0.55 : 1}
                strokeWidth={1}
                shapeRendering="crispEdges"
              />
              <text
                x={rx}
                y={MARGIN.top - 6}
                textAnchor="middle"
                className="numeral"
                fontSize={11}
                fill="var(--c-text-tertiary)"
              >
                age {Math.round(layer.targetAge)}
              </text>
            </g>
          )
        })}

        {/* ── The moment it works ── */}
        {ordered.map(({ layer, frame }) => (
          <Crossing
            key={`cross-${layer.id}`}
            frame={frame}
            hue={layer.hueToken}
            active={layer.active}
            comparing={comparing}
            celebrating={celebrating && layer.active}
            x={x}
            y={y}
          />
        ))}

        {/* ── Axes ── */}
        <g aria-hidden="true">
          {yTicks.map((tick) => (
            <text
              key={tick}
              x={MARGIN.left - 8}
              y={y(tick)}
              textAnchor="end"
              dominantBaseline="middle"
              className="numeral"
              fontSize={11}
              fill="var(--c-text-muted)"
            >
              {formatINRTick(tick)}
            </text>
          ))}
          {xTicks.map((age) => (
            <text
              key={age}
              x={x(age)}
              y={MARGIN.top + plotHeight + 18}
              textAnchor="middle"
              className="numeral"
              fontSize={11}
              fill="var(--c-text-muted)"
            >
              {age}
            </text>
          ))}
        </g>

        {/* ── Scrubber: the active layer only ──
            Two stacked readouts on a 360px screen is not a thing worth
            attempting, and the comparison's numbers are already stated in words
            by the delta line above the chart. */}
        {scrubIndex !== null && scrubAge !== null && (
          <g pointerEvents="none">
            <line
              x1={x(scrubAge)}
              x2={x(scrubAge)}
              y1={MARGIN.top}
              y2={MARGIN.top + plotHeight}
              stroke="var(--c-text-tertiary)"
              strokeWidth={1}
            />
            {(['p90', 'p50', 'p10'] as const).map((key) => (
              <circle
                key={key}
                cx={x(scrubAge)}
                cy={y(active.frame[key][Math.min(scrubIndex, active.frame[key].length - 1)])}
                r={key === 'p50' ? 4 : 3}
                fill={
                  comparing
                    ? `var(${active.layer.hueToken})`
                    : key === 'p50'
                      ? 'var(--c-fan-median)'
                      : 'var(--c-fan-3)'
                }
                stroke="var(--c-bg-base)"
                strokeWidth={2}
              />
            ))}
          </g>
        )}
      </svg>

      {/* Tooltip is HTML, not SVG: real text rendering, real line wrapping, and
          it can escape the SVG's bounds near the edges. */}
      {scrubIndex !== null && scrubAge !== null && active.layer.result !== null && (
        <Tooltip
          x={x(scrubAge)}
          containerWidth={width}
          age={scrubAge}
          label={comparing ? active.layer.label : null}
          median={at(active.frame.p50, scrubIndex)}
          low={at(active.frame.p10, scrubIndex)}
          high={at(active.frame.p90, scrubIndex)}
          required={at(active.frame.required, scrubIndex)}
          probability={at(active.layer.result.successByAge, scrubIndex)}
        />
      )}
    </div>
  )
}

const at = (series: Float32Array, index: number): number =>
  series[Math.min(Math.max(0, index), series.length - 1)]

const clampAge = (age: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, age))

/** Two scenarios retiring at the same age would otherwise stack two identical
 *  rules and print the label twice on top of itself. */
function dedupeByAge<T extends { layer: FanLayer }>(entries: T[]): T[] {
  /*
   * Active first, because the survivor's hue is what gets drawn.
   *
   * `ordered` is deliberately sorted inactive-first for compositing, and keeping
   * the first match there painted the shared rule in the hue of the scenario the
   * user is NOT editing — inverting the focus signal the whole encoding rests
   * on. It is not an edge case: adding a comparison duplicates the active plan,
   * so both targets coincide the instant Compare is pressed.
   */
  const byFocus = [...entries].sort(
    (a, b) => Number(b.layer.active) - Number(a.layer.active),
  )
  const seen = new Set<number>()
  return byFocus.filter((entry) => {
    const age = Math.round(entry.layer.targetAge)
    if (seen.has(age)) return false
    seen.add(age)
    return true
  })
}

/** Solid up to `from`, falling to FADE_FLOOR by `to`. */
function FadeMask({
  id,
  from,
  to,
  rect,
}: {
  id: string
  from: number
  to: number
  rect: { x: number; y: number; width: number; height: number }
}) {
  return (
    <>
      {/* userSpaceOnUse with spreadMethod pad: everything left of `from` inherits
          the first stop, so the fan is at full strength right up to its target. */}
      <linearGradient id={`${id}-g`} gradientUnits="userSpaceOnUse" x1={from} y1={0} x2={Math.max(from + 1, to)} y2={0}>
        <stop offset="0%" stopColor="#fff" stopOpacity={1} />
        <stop offset="100%" stopColor="#fff" stopOpacity={FADE_FLOOR} />
      </linearGradient>
      <mask id={id}>
        <rect {...rect} fill={`url(#${id}-g)`} />
      </mask>
    </>
  )
}

/** Today's single-fan treatment, unchanged. */
function SoloBands({
  frame,
  uid,
  x,
  y,
}: {
  frame: FanFrame
  uid: string
  x: LinearScale
  y: LinearScale
}) {
  return (
    <>
      <path d={bandPath(frame.ages, frame.p10, frame.p90, x, y)} fill={`url(#${uid}-outer)`} />
      <path d={bandPath(frame.ages, frame.p25, frame.p75, x, y)} fill={`url(#${uid}-inner)`} />
      {/* Median: a wide, faint pass under a crisp one reads as a glow at a
          fraction of the cost of an SVG blur filter, which is expensive to
          composite on a phone every frame. */}
      <path
        d={linePath(frame.ages, frame.p50, x, y)}
        fill="none"
        stroke="var(--c-fan-median)"
        strokeWidth={7}
        strokeOpacity={0.16}
        strokeLinecap="round"
      />
      <path
        d={linePath(frame.ages, frame.p50, x, y)}
        fill="none"
        stroke="var(--c-fan-median)"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  )
}

/**
 * One scenario in comparison mode: the same two bands and median either way, at
 * half the weight when it is not the one being edited.
 *
 * Fill opacity comes from the token layer rather than from constants here,
 * because the value that reads as "recessive peer" on near-black reads as
 * "disabled" on white.
 */
function ComparisonBands({
  frame,
  hue,
  active,
  x,
  y,
}: {
  frame: FanFrame
  hue: string
  active: boolean
  x: LinearScale
  y: LinearScale
}) {
  const outer = active ? '0.3' : 'var(--c-fan-inactive-outer)'
  const inner = active ? '0.4' : 'var(--c-fan-inactive-inner)'
  const colour = `var(${hue})`

  return (
    <>
      <path
        d={bandPath(frame.ages, frame.p10, frame.p90, x, y)}
        fill={colour}
        style={{ fillOpacity: outer }}
      />
      <path
        d={bandPath(frame.ages, frame.p25, frame.p75, x, y)}
        fill={colour}
        style={{ fillOpacity: inner }}
      />
      {active && (
        <path
          d={linePath(frame.ages, frame.p50, x, y)}
          fill="none"
          stroke={colour}
          strokeWidth={7}
          strokeOpacity={0.18}
          strokeLinecap="round"
        />
      )}
      <path
        d={linePath(frame.ages, frame.p50, x, y)}
        fill="none"
        stroke={colour}
        strokeWidth={active ? 2.4 : 1.6}
        strokeOpacity={active ? 1 : 0.85}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  )
}

/**
 * Where the median first clears the requirement.
 *
 * Gold marks the moment, and in comparison mode the moment belongs to the
 * scenario being edited: the comparison gets a hollow ring in its own hue
 * instead. Two solid gold discs land about 15px apart at 360px and read as a
 * single smudge — the collision is worst on exactly the device most of the
 * traffic arrives on.
 */
function Crossing({
  frame,
  hue,
  active,
  comparing,
  celebrating,
  x,
  y,
}: {
  frame: FanFrame
  hue: string
  active: boolean
  comparing: boolean
  celebrating: boolean
  x: LinearScale
  y: LinearScale
}) {
  // Interpolated between year points so the marker sits on the crossing rather
  // than snapping to the nearest birthday.
  const crossing = findCrossing(frame.ages, frame.p50, frame.required)
  if (crossing === null) return null

  const cx = x(crossing.age)
  const cy = y(crossing.value)

  if (comparing && !active) {
    return <circle cx={cx} cy={cy} r={4.5} fill="none" stroke={`var(${hue})`} strokeWidth={2} />
  }

  return (
    <g>
      <circle cx={cx} cy={cy} r={9} fill="var(--c-fire)" opacity={0.22}>
        <animate attributeName="r" values="7;13;7" dur="2.6s" repeatCount="indefinite" />
      </circle>

      {/*
        The signature moment: one ring expanding out of the marker as the odds
        cross into "likely". Keyed on `celebrating` so React remounts the node
        and the SVG animation restarts from zero — without a changing key it
        would only ever play once per page load.

        One ring, not a burst. This is somebody's retirement, and the difference
        between a product that feels confident and one that feels like a slot
        machine is entirely in how much it does here.
      */}
      {celebrating && (
        <circle
          key="celebration"
          cx={cx}
          cy={cy}
          fill="none"
          stroke="var(--c-fire-bright)"
          strokeWidth={2}
          pointerEvents="none"
        >
          <animate attributeName="r" values="6;42" dur="1.2s" fill="freeze" />
          <animate attributeName="opacity" values="0.9;0" dur="1.2s" fill="freeze" />
          <animate attributeName="stroke-width" values="2.5;0.5" dur="1.2s" fill="freeze" />
        </circle>
      )}

      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill="var(--c-fire-bright)"
        stroke={comparing ? `var(${hue})` : 'var(--c-bg-base)'}
        strokeWidth={2}
      />
    </g>
  )
}

interface TooltipProps {
  x: number
  containerWidth: number
  age: number
  /** Which scenario the numbers belong to. Null when there is only one. */
  label: string | null
  median: number
  low: number
  high: number
  required: number
  probability: number
}

function Tooltip({
  x,
  containerWidth,
  age,
  label,
  median,
  low,
  high,
  required,
  probability,
}: TooltipProps) {
  const WIDTH = 178
  // Keep the card inside the container however close to an edge the finger gets.
  const left = Math.max(4, Math.min(containerWidth - WIDTH - 4, x - WIDTH / 2))

  return (
    <div
      style={{
        position: 'absolute',
        left,
        top: 4,
        width: WIDTH,
        pointerEvents: 'none',
        background: 'var(--c-bg-elevated)',
        border: '1px solid var(--c-border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-md)',
        padding: '10px 12px',
      }}
    >
      <div
        style={{
          fontSize: 'var(--text-micro)',
          letterSpacing: 'var(--tracking-label)',
          textTransform: 'uppercase',
          color: 'var(--c-text-tertiary)',
        }}
      >
        {label === null ? `At age ${Math.round(age)}` : `${label} · at age ${Math.round(age)}`}
      </div>
      <div
        className="numeral-hero"
        style={{ fontSize: 22, color: 'var(--c-text-primary)', marginTop: 2 }}
      >
        {formatINRCompact(median)}
      </div>
      <dl style={{ margin: '8px 0 0', display: 'grid', gap: 3 }}>
        <TooltipRow
          label="Range"
          value={`${formatINRCompact(low)} – ${formatINRCompact(high)}`}
        />
        <TooltipRow label="Needed" value={formatINRCompact(required)} accent />
        <TooltipRow label="Chance" value={formatPercent(probability)} />
      </dl>
    </div>
  )
}

function TooltipRow({
  label,
  value,
  accent = false,
}: {
  label: string
  value: string
  accent?: boolean
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
      <dt style={{ fontSize: 'var(--text-micro)', color: 'var(--c-text-muted)' }}>
        {label}
      </dt>
      <dd
        className="numeral"
        style={{
          margin: 0,
          fontSize: 'var(--text-micro)',
          color: accent ? 'var(--c-fire)' : 'var(--c-text-secondary)',
        }}
      >
        {value}
      </dd>
    </div>
  )
}

function describe(layers: FanLayer[]): string {
  const parts = layers
    .filter((layer) => layer.result !== null)
    .map((layer) => {
      const result = layer.result as SimResult
      const prefix = layers.length > 1 ? `Scenario ${layer.label}: ` : ''
      return (
        `${prefix}${formatPercent(result.probabilityAtTarget)} chance of reaching financial ` +
        `independence by age ${Math.round(layer.targetAge)}. Median corpus at that age ` +
        `${formatINRCompact(result.medianCorpusAtTarget)}, against a requirement of ` +
        `${formatINRCompact(result.requiredAtTarget)}.`
      )
    })

  if (parts.length === 0) return 'Projection loading'

  const first = layers.find((layer) => layer.result !== null)?.result as SimResult
  const span = `Projected corpus from age ${first.ages[0]} to ${first.ages[first.ages.length - 1]}. `
  return span + parts.join(' ')
}
